import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Play, Youtube } from "lucide-react";
import Newsletter from "@/components/Newsletter";
import { FadeIn, MaskedLine } from "@/components/Reveal";
import { CLIPS, YOUTUBE_URL } from "@/data/content";
import useSEO from "@/hooks/useSEO";

export default function Clip() {
  const { id } = useParams();
  const clip = CLIPS.find((c) => c.id === id);
  useSEO(
    clip ? clip.seoTitle : "Watch | The Cap Chronicles",
    clip ? clip.seoDesc : "Watch original clips and read-aloud stories for kids ages 4-8 from The Cap Chronicles."
  );

  if (!clip) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-48 text-center">
        <h1 className="font-serif text-4xl tracking-tight">This clip hasn't been released yet</h1>
        <Link to="/media" data-testid="clip-not-found-back" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-forest">
          <ArrowLeft size={15} /> Back to media
        </Link>
      </main>
    );
  }

  return (
    <main>
      <section className="mx-auto max-w-5xl px-6 pb-16 pt-36 md:px-12 md:pt-44" data-testid="clip-hero">
        <FadeIn>
          <Link to="/media" data-testid="clip-back-link" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-charcoal/50 transition-colors hover:text-forest">
            <ArrowLeft size={14} /> Media
          </Link>
        </FadeIn>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <FadeIn delay={0.05}>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky">{clip.kind} · {clip.length}</p>
          </FadeIn>
        </div>
        <h1 className="mt-4 font-serif text-5xl leading-[1.05] tracking-tight md:text-6xl" data-testid="clip-title">
          <MaskedLine delay={0.15}>{clip.title}</MaskedLine>
        </h1>
        <FadeIn delay={0.3}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-charcoal/70 md:text-lg">{clip.description}</p>
        </FadeIn>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-16 md:px-12" data-testid="clip-player-section">
        <FadeIn delay={0.1}>
          {clip.videoId ? (
            <div className={`overflow-hidden rounded-3xl shadow-2xl ${clip.short ? "mx-auto max-w-sm" : ""}`}>
              <iframe
                className={`w-full ${clip.short ? "aspect-[9/16]" : "aspect-video"}`}
                src={`https://www.youtube.com/embed/${clip.videoId}`}
                title={`${clip.title} | The Cap Chronicles`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="clip-player"
              />
            </div>
          ) : (
            <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" data-testid="clip-youtube-link" className="group relative block overflow-hidden rounded-3xl shadow-xl">
              <img src={clip.img} alt={clip.title} className="aspect-video w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-charcoal/45" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-cream/90 text-charcoal shadow-xl transition-transform duration-300 group-hover:scale-110">
                  <Play size={22} fill="currentColor" />
                </span>
                <p className="font-serif text-2xl italic text-cream md:text-3xl">Premiering soon on YouTube</p>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-cream/75">Tap to visit the channel</p>
              </div>
            </a>
          )}
        </FadeIn>

        <FadeIn delay={0.2} className="mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" data-testid="clip-subscribe-button" className="group inline-flex items-center gap-3 rounded-full bg-forest px-8 py-4 text-sm font-semibold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-[1.03]">
            <Youtube size={17} /> Subscribe on YouTube
          </a>
          <Link to="/stories" data-testid="clip-stories-link" className="group inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-forest">
            Explore the storybooks <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeIn>
      </section>

      <Newsletter />
    </main>
  );
}
